/**
 * Sign in — one button, matching fh-hris.
 *
 * Credentials, MFA and lockout are Entra ID's job: the button sends the browser
 * to /api/auth/login, which redirects to login.microsoftonline.com and back.
 * The only UI this page owns is the error strip for a callback that came back
 * with ?error=…
 */

import { FiAlertCircle } from "react-icons/fi";
import logo from "@/assets/images/fh-horizontal-logo.png";
import { Button, Card } from "@/components/ui";

// Distinguished on purpose: "not in the group" needs a person to grant access,
// while a failed sign-in is worth retrying. Telling someone to try again when
// they need an access grant wastes their time and an admin's.
const ERROR_COPY: Record<string, string> = {
  not_authorised:
    "Your account signed in, but is not a member of the Playground group — ask an administrator to add you.",
  expired: "The sign-in took too long and expired. Try again.",
  state: "The sign-in could not be validated. Try again.",
  failed: "Microsoft could not complete the sign-in. Try again.",
};

function MicrosoftMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 21 21" aria-hidden>
      <rect x="0" y="0" width="10" height="10" fill="#F25022" />
      <rect x="11" y="0" width="10" height="10" fill="#7FBA00" />
      <rect x="0" y="11" width="10" height="10" fill="#00A4EF" />
      <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

export default function Login() {
  const params = new URLSearchParams(window.location.search);
  const errorKey = params.get("error");
  const error = errorKey ? (ERROR_COPY[errorKey] ?? ERROR_COPY.failed) : null;

  const signIn = () => {
    const next = params.get("next");
    const safe = next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
    window.location.href = `/api/auth/login?next=${encodeURIComponent(safe)}`;
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-fh-surface-soft px-4">
      <Card className="w-full max-w-sm p-8">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src={logo} alt="Firehorse" className="mb-4 h-9" />
          <h1 className="text-xl font-semibold text-fh-text-strong">Firehorse Playground</h1>
          <p className="mt-1 text-fh-body text-fh-text-secondary">
            Sign in with your Firehorse Microsoft account.
          </p>
        </div>

        {error && (
          <p
            role="alert"
            className="mb-4 flex items-start gap-2 rounded-fh-input bg-fh-badge-red-bg px-3 py-2 text-fh-body text-fh-badge-red-text shadow-[inset_0_0_0_1px_var(--color-fh-badge-red-stroke)]"
          >
            <FiAlertCircle className="mt-0.5 shrink-0" aria-hidden />
            {error}
          </p>
        )}

        <Button className="w-full gap-2.5" onClick={signIn}>
          <MicrosoftMark />
          Sign in with Microsoft
        </Button>

        <p className="mt-4 text-center text-fh-meta text-fh-text-muted">
          Access is managed in Entra ID. Passwords and authenticator codes belong to your
          Microsoft account — Playground never sees them.
        </p>
      </Card>
    </div>
  );
}
